/**
 * <ev-cart-subscribe>: the subscribe switch on a cart line, in the cart drawer
 * (Task 11) and on the /cart page (Task 25), where the error is announced through
 * the fallback status region assets/ev-cart-drawer.js adds.
 *
 * Markup: snippets/ev-cart-line-subscribe.liquid. The control is a native
 * <input type="checkbox" role="switch">, so click, Space and focus are native.
 * Its id derives from the line key, and every listener is delegated from the
 * host and resolves the input when the event fires: Horizon's morph may keep
 * the host while it swaps or moves the nodes inside it.
 *
 * Switching on posts /cart/change.js { id, quantity, selling_plan, sections,
 * sections_url } with the line key (data-key, ruling R40) and the variant's
 * first SKIO plan (data-selling-plan); switching off posts the same with
 * selling_plan: null. A CartLinesUpdateEvent from @shopify/events carries the
 * request promise, so Horizon's cart-items-component morphs the drawer from
 * the returned section HTML (the pattern in assets/product-form.js). The line
 * key changes with the plan, so the row is replaced: focus then moves to the
 * switch rendered for the same variant in the new state.
 *
 * While a request is in flight the switch is aria-disabled (never disabled, so
 * it keeps focus), other line edits are blocked (Horizon's cart-items-disabled)
 * and further toggles are cancelled. Enter is always cancelled: the cart form's
 * default button is Checkout. On failure the switch reverts, the error is
 * shown, linked with aria-describedby and announced.
 *
 * Static helpers snapshotFocus() and restoreFocus() are shared with
 * assets/ev-cart-drawer.js (through customElements.get) so a re-render that
 * moves the focused switch, such as the free-gift cleanup, puts focus back.
 */
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const FOCUS_WAIT_MS = 4000;
const ANNOUNCE_CLEAR_MS = 5000;
const INPUT = '[data-ev-subscribe-input]';

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

/** Section ids of every cart-items-component on the page (the drawer, and the cart page when present). */
function sectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

function changeUrl() {
  const base = window.Theme?.routes?.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change`;
  return `${base}.js`;
}

/**
 * @typedef {{ variantId: string, state: string, line: string, input: Element }} FocusSnapshot
 */

class EvCartSubscribe extends HTMLElement {
  /**
   * The switch that has focus right now, described so it can be found after a re-render.
   * @returns {FocusSnapshot | null}
   */
  static snapshotFocus() {
    const active = document.activeElement;
    const host = active instanceof Element && active.matches(INPUT) ? active.closest('ev-cart-subscribe') : null;
    if (!(host instanceof HTMLElement) || !active) return null;
    return {
      variantId: host.dataset.variantId || '',
      state: host.dataset.state || '',
      line: host.dataset.line || '',
      input: active,
    };
  }

  /**
   * Focus the switch for the snapshot's variant and state once it is rendered, but only
   * when focus was lost with the old node (or never left it). Focus the shopper moved to
   * another control while the request ran stays there (P1), even when the old switch node
   * has since left the document.
   * @param {FocusSnapshot} snapshot
   * @param {{ until?: () => boolean }} [options] - an extra condition to wait for, such as a morph landing.
   * @returns {Promise<boolean>} Whether a matching switch was found in time.
   */
  static async restoreFocus(snapshot, { until } = {}) {
    const selector = `ev-cart-subscribe[data-variant-id="${CSS.escape(snapshot.variantId)}"][data-state="${CSS.escape(snapshot.state)}"]`;
    const deadline = performance.now() + FOCUS_WAIT_MS;

    while (performance.now() < deadline) {
      await nextFrame();
      if (until && !until()) continue;
      const matches = [...document.querySelectorAll(selector)];
      if (!matches.length) continue;

      const target =
        matches.find((el) => el instanceof HTMLElement && el.dataset.line === snapshot.line) || matches[0];
      const next = target.querySelector(INPUT);
      const active = document.activeElement;
      const focusLost =
        !active || active === document.body || active === snapshot.input || active.tagName === 'DIALOG';
      if (next instanceof HTMLElement && focusLost && document.activeElement !== next) next.focus();
      return true;
    }
    return false;
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /** Resolved on every use: a morph can replace the input while this host stays. */
  get #input() {
    const input = this.querySelector(INPUT);
    return input instanceof HTMLInputElement ? input : null;
  }

  get #busy() {
    return this.#input?.getAttribute('aria-disabled') === 'true' || Boolean(this.closest('.cart-items-disabled'));
  }

  /** @param {Event} event */
  #fromInput(event) {
    return event.target instanceof Element && event.target.matches(INPUT);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (this.#fromInput(event) && event.key === 'Enter') event.preventDefault();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // A label click reaches the input as its own click; only the input's click toggles.
    // Cancelling it also cancels the checkbox toggle.
    if (this.#fromInput(event) && this.#busy) event.preventDefault();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!this.#fromInput(event)) return;
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#toggle(input, input.checked);
  };

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} on
   */
  async #toggle(input, on) {
    const cartItems = this.closest('cart-items-component');
    const variantId = this.dataset.variantId || '';
    const key = this.dataset.key || '';
    const line = this.dataset.line || '';
    const quantity = Number(this.dataset.quantity);
    const sellingPlan = on ? Number(this.dataset.sellingPlan) : null;
    const sections = sectionIds();

    input.setAttribute('aria-disabled', 'true');
    cartItems?.classList.add('cart-items-disabled');
    this.#clearError();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(changeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          selling_plan: sellingPlan,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status || data.errors) {
        const message = data.description || (typeof data.errors === 'string' ? data.errors : '') || data.message;
        throw new Error(message || '');
      }

      cartItems?.classList.remove('cart-items-disabled');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections ?? undefined,
          items: data.items,
          itemCount: Number(data.item_count) || 0,
          source: 'ev-cart-subscribe',
          didError: false,
        },
      });
      const restored = await EvCartSubscribe.restoreFocus({ variantId, state: on ? 'on' : 'off', line, input });
      // No re-render arrived, or the morph kept this node: release the switch.
      if (!restored || input.isConnected) input.removeAttribute('aria-disabled');
    } catch (error) {
      deferred.reject(error);
      cartItems?.classList.remove('cart-items-disabled');
      input.checked = !on;
      input.removeAttribute('aria-disabled');
      const message = (error instanceof Error && error.message) || this.dataset.errorMessage || '';
      this.#showError(message);
      this.dispatchEvent(new CartErrorEvent({ error: message || 'Cart update failed', code: 'INVALID' }));
    }
  }

  #clearError() {
    const error = this.querySelector('[data-ev-subscribe-error]');
    const input = this.#input;
    if (!error || !input) return;
    error.textContent = '';
    error.hidden = true;
    input.setAttribute('aria-describedby', `${input.id}-description`);
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-ev-subscribe-error]');
    const input = this.#input;
    if (!error || !input || !message) return;
    error.textContent = message;
    error.hidden = false;
    input.setAttribute('aria-describedby', `${input.id}-description ${error.id}`);

    const announcer =
      this.closest('cart-drawer-component')?.querySelector('[data-ev-cart-announcer]') ||
      document.querySelector('[data-ev-cart-announcer-fallback]');
    if (announcer) {
      announcer.textContent = '';
      requestAnimationFrame(() => {
        announcer.textContent = message;
        // The visible error stays by the switch; the spoken copy clears itself (N2).
        setTimeout(() => {
          if (announcer.textContent === message) announcer.textContent = '';
        }, ANNOUNCE_CLEAR_MS);
      });
    }
  }
}

if (!customElements.get('ev-cart-subscribe')) {
  customElements.define('ev-cart-subscribe', EvCartSubscribe);
}
